export * from './crm'
import ky from 'ky'
import {
  BookWithMe,
  Product,
  MeetingsProductType,
  LicensesTableResponse,
  MeetingsActiveUser,
  UserOnboardingSessionStep,
  ScrollableTableResponse,
  ScrollableTableRequest,
  ScrollableTableRequestV2,
  LicensesTableRequest,
  HubspotAdminResponse,
  LastGeneratedTemplate,
  AccountOnboardingSessionStep,
  AccountSearchResponse,
  CreateContactPost,
  CreateLeadPost,
  Reminder,
  ReminderTable,
  TemplateDetails,
  BookerCalendarEvent,
  GlobalSettings,
  UserAccess,
  CrmEntity,
  CrmFieldsResponse,
  CrmOperator,
  CrmRouter,
  CrmRouterData,
  Case,
  ReminderStatus,
  SalesforceQueuesResponse,
  CrmSyncStatus,
  CrmRouterRequest,
  BookerRouteSettings,
  InboundRouter,
  InboundRouterByLink,
  GetInfoDataType,
  WorkspaceUser,
  OrganizationDetails,
  BookerVideoCallStatus,
  UserTableUpdateEntry,
  UpdateTableV2Payload,
  MeetingsActiveUserBrief,
} from '@chilipiper/api-type-def'
import { RulesExecutionResponse } from '@chilipiper/api-type-def/src/routerDebug'
import {
  OrganizationStyles,
  OrganizationSettings,
  Organization,
} from '@chilipiper/api-type-def/src/personal-pages'
import { nanoid } from 'nanoid'
import {
  BookerCalendar,
  BookerInfoResponse,
  BookerPreferences,
  BookerReassign,
  BookerRelatedTo,
  BookerRescheduleReassign,
  BookerRules,
  BookMe,
  BookRequest,
  BusinessCalendarsConnection,
  BusinessUserConnections,
  BusinessWithMicrosoft,
  CreateOnboardingAccountPayload,
  CrmContactTemplateSettingsPayload,
  CrmContactWorkspaceSettings,
  CrmContactWorkspaceSettingsPayload,
  CrmEventWorkspaceSettings,
  CrmEventWorkspaceSettingsPayload,
  CrmTemplateEventSettingsPayload,
  DraftInviteToMeetings,
  ExpiredDays,
  DraftCrmRouter,
  Form,
  GuestSession,
  IntercomStatusResponse,
  InviteToMeetings,
  OnboardingSearchUser,
  OnboardingSteps,
  ParticipantByEmailResponse,
  PersonalCrmEventSettings,
  PersonalCrmEventSettingsPayload,
  PersonalTemplateCrmEventSettingsPayload,
  Placeholder,
  PlaceholderDAO,
  PrintableBrief,
  ProductTypes,
  ProfileDetailsResponse,
  Prospect,
  Queue,
  RecentWorkspace,
  Report,
  SalesforceUser,
  SessionActions,
  Template,
  ThirdPartyConnection,
  TimezoneUserSettingsPayload,
  TimezoneUserSettingsResponse,
  Translations,
  TwilioConnectBody,
  TwilioPhoneStatusResponse,
  TwilioPhoneVerifyPayload,
  TwilioPhoneVerifyResponse,
  TwilioStatusResponse,
  UsersAtAllResponse,
  UserSearch,
  UserSettings,
  UserWithSalesforcePaged,
  UserWithSalesforceRequest,
  Workspace,
  WorkspaceLicenseUsage,
  WorkspacePreferences,
  WorkspaceType,
  WorkspaceVisibility,
  ReportHistoryItem,
  CurrentBook,
  PhoneParseMessageParams,
  PhoneParseString,
  BookMeRouter,
  BookerInitiateCall,
  TemplateAssigneeName,
} from '@chilipiper/api-type-def'
import {
  BookerCalendarParam,
  BookerRulesParam,
  BookerInfoParam,
  BookerUserEventsParam,
  BookerMemebersActivateParam,
  BookerMemebersUpdateConciergeLicensesParam,
  HandoffCalendarParam,
  WorkingHoursParam,
  BookerUsersNonActiveParam,
  BookerUsersNonActiveResponse,
  OriginalEventsCalendarParam,
} from '@chilipiper/api-type-def/src/payload/booker'
import { TeamUsersParam } from '@chilipiper/api-type-def/src/payload/workspaces'
import { ParseParam } from '@chilipiper/api-type-def/src/payload/templates'
import { ParseResponse } from '@chilipiper/api-type-def/src/response/templates'
import { LicensedProduct, TrialRaw } from '@chilipiper/api-type-def/src/license'
import { BookingLink, BookingLinkDAO } from '@chilipiper/api-type-def/src/bookingLinks'
import { Licenses, LicenseAccess } from '@chilipiper/api-type-def/src/license'
import { Options as RequestOptions } from 'ky'
import { api } from './endpoint'
import {
  post,
  get,
  del,
  put,
  reportingGet,
  reportingPost,
  profileUpload,
  profileGet,
  profilePatch,
  patch,
  rootApiGet,
  rootApiPost,
} from './api'
import { get as getV2, post as postV2, put as putV2, del as delV2 } from './api-v2'
import { withScope } from './sentry'
import { PageParams } from './types'

export interface StandardSuccessResponse {
  success: boolean
}

export interface StandardErrorResponse {
  error: boolean
  message: string
  success: boolean
}

export interface CodedErrorResponse {
  arguments: unknown
  errorCode: string
  errorMessage: string
}

export interface UsersAtAllParam {
  exclude?: string[]
  include?: string[]
  page: number
  query: string
  size: number
}

export interface SaveWorkspaceParam {
  color?: string
  emoji?: string
  id?: string
  integrations?: string[]
  logo?: string
  meetingsType: MeetingsProductType
  name: string
  order?: number
  workspaceType: WorkspaceType
}

const apiRoot = api('/api/v1')

const guestService = ky.create({
  prefixUrl: apiRoot,
  mode: 'cors',
  timeout: false,
  credentials: 'include',
  headers: {
    Accept: '*/*',
    'content-type': 'application/json',
  },
  hooks: {
    beforeRequest: [
      request => {
        const requestId = nanoid()

        withScope(scope => {
          scope.setTag('request_id', requestId)
        })

        request.headers.set('x-request-id', requestId)
      },
    ],
  },
})

const localesService = ky.create({
  prefixUrl: `${api()}/assets/locale`,
  mode: 'cors',
  timeout: false,
  headers: {
    Accept: '*/*',
    'x-request-id': nanoid(),
    'content-type': 'application/json',
    'Cache-Control': 'no-store,no-cache,must-revalidate',
  },
})

export const fetchTranslations = (locale: string) =>
  localesService(`${locale}.json?v=3`).json<Translations>()

export const getWorkspaceType = (workspaceId: string) =>
  get(`workspace-type/${workspaceId}`).json<WorkspaceType>()

export const setGuestAttendance = (
  meetingId: string,
  guestEmail: string,
  attendance: 'CheckedIn' | 'NoShow'
) => {
  return post(`no-show/set-guest/${meetingId}/${guestEmail}/${attendance}`).json()
}

export const reports = {
  byId(id: string, domain: string) {
    return guestService(`reschedule/find-report/${id}/${domain}`).json<Report>()
  },
  reassignById(reportId: string) {
    return get(`reassign/find-report/${reportId}`).json<string | null>()
  },
  byEventId(eventId: string) {
    return reportingGet(`reports/event/${eventId}`).json<Report[]>()
  },
  meetings: {
    cancel(reportId: string, domain: string) {
      return guestService
        .delete(`reports/meeting/cancel/${reportId}`, {
          searchParams: {
            domain,
          },
        })
        .json<{ assigneeSFId: string; eventId: string }>()
    },
  },
  briefs(ids: string[]) {
    return reportingPost('reports/briefs', {
      body: JSON.stringify(ids),
    }).json<PrintableBrief>()
  },
}

export const admin = {
  daysToExpire() {
    return get('admin/days-to-expire').json<ExpiredDays>()
  },
}

export const bookMe = {
  book(payload: BookRequest) {
    return post('book-me', {
      json: payload,
    }).json<BookWithMe>()
  },
  session(domain: string, route: string) {
    return guestService(`book-me/session/${domain}${route}`).json<GuestSession>()
  },
  byId(gid: string) {
    return get(`book-me/by-id/${gid}`).json<BookMe>()
  },
  lastMomentCheck(info: GetInfoDataType) {
    return post('book-me/last-moment-check', {
      json: info,
    }).json<{ busy: boolean }>()
  },
  lastMomentCheckWithoutGuestSession(info: GetInfoDataType) {
    // Used outside booking-app, when user is authenticated on CP
    return post('book-me/last-moment-check/session', {
      json: info,
    }).json<{ busy: boolean }>()
  },
  removeGuestSession() {
    return get('book-me/remove-guest-session').json()
  },
  current(sessionId: string, domain: string) {
    return get(`book-me/current/${sessionId}?domain=${domain}`).json<CurrentBook | null>()
  },
  getAssigneeNameById: (assigneeId: string) => {
    return get(`book-me/name-by-id/${assigneeId}`).json<string>()
  },
  router: (domain: string, search?: string) => {
    return get(`book-me/router/${domain}${search}`).json<BookMeRouter>()
  },
}

export const members = {
  hasCalendarConnected(memberIds: string[]) {
    return post('business/users/check-calendars-connection', {
      body: JSON.stringify(memberIds),
    }).json<BusinessCalendarsConnection>()
  },
  getEmail(id: string) {
    return get(`business/user/email-by-id/${id}`).json<string>()
  },
  getLinkName(id: string) {
    return get(`business/user/link-name-by-id/${id}`).json<string>()
  },
  checkMicrosoft() {
    return get(`business/with-microsoft`).json<BusinessWithMicrosoft>()
  },
  checkUserConnections() {
    return get(`business/user/connections`).json<BusinessUserConnections[]>()
  },
  checkUsersConnections(users: string[]) {
    const query = users.map(user => `ids=${user}`).join('&')
    return get(`business/user/connections-by-ids?${query}`).json<
      Record<string, BusinessUserConnections[]>
    >()
  },
}

export const booker = {
  queues: {
    activeByWorkspace(workspaceId: string, handoff = true) {
      return get(`booker/active-queues/${workspaceId}?handoff=${handoff}`).json<Queue[]>()
    },
    get(queueId: string) {
      return get(`booker/queue/${queueId}`).json<Queue>()
    },
    duplicate(queueId: string, queueName: string) {
      return post(`booker/queue/${queueId}/duplicate`, {
        json: {
          name: queueName,
        },
      }).json<Queue>()
    },
    updateQueue(queue: Queue) {
      return post(`booker/queue`, {
        json: queue,
      }).json<string>()
    },
    getAll(workspaceId: string) {
      return get(`booker/queues/${workspaceId}`).json<Queue[]>()
    },
    getQueuesByUserIds(
      userIds: string[],
      pageParams: PageParams = {}
    ): Promise<ScrollableTableResponse<Queue>> {
      return post('booker/active-queues-by-members', {
        json: {
          members: userIds,
          ...pageParams,
        },
      }).json()
    },
    getQueuesByUserIdsAndWorkspaceIds(userIds: string[], workspaceIds: string[]) {
      return post('booker/active-queues-by-workspaces-with-users', {
        json: { userObjectIds: userIds, workspaceObjectIds: workspaceIds },
      }).json<Queue[]>()
    },
  },
  users: {
    nonActive(params: BookerUsersNonActiveParam) {
      return post('booker/users/non-active', {
        json: params,
      }).json<BookerUsersNonActiveResponse>()
    },
  },
  members: {
    licenses(usersIds: string[]) {
      return post('booker/members/licenses', {
        json: usersIds,
      }).json<Record<string, MeetingsActiveUser['licences']>>()
    },
    activate(params: BookerMemebersActivateParam) {
      return post('booker/members/activate', {
        json: params,
      }).json()
    },
    updateConciergeLicenses(params: BookerMemebersUpdateConciergeLicensesParam) {
      return post('booker/members/concierge-licenses', {
        json: params,
      }).json()
    },
  },
  rules(params: BookerRulesParam) {
    return post('booker/rules', {
      json: params,
    }).json<BookerRules[]>()
  },
  info(params: BookerInfoParam) {
    return post('booker/info', {
      json: params,
    }).json<BookerInfoResponse[]>()
  },
  calendar(params: OriginalEventsCalendarParam) {
    return post('booker/original-events-calendar', {
      json: params,
    }).json<BookerCalendar>()
  },
  calendarSlots(params: BookerCalendarParam) {
    return post('booker/calendar', {
      json: params,
    }).json<BookerCalendar>()
  },
  handoffExists() {
    return get('booker/handoff').json<{ exists: boolean }>()
  },
  handoffCalendar(params: HandoffCalendarParam) {
    return post('booker/handoff-calendar', {
      json: params,
    }).json<BookerCalendar>()
  },
  usersEvents(params: BookerUserEventsParam) {
    return post('booker/users-events', {
      json: params,
    }).json<BookerCalendar>()
  },
  book(params: BookRequest) {
    /*
      If we retry this request and there is some kind of slowlyness, user might book multiple meetings
    */
    return post('booker/book', { json: params, retry: 1 }).json<BookerRescheduleReassign>()
  },
  reschedule(params: BookRequest) {
    return post('booker/reschedule', {
      json: params,
    }).json<BookerRescheduleReassign>()
  },
  reassign(params: BookRequest) {
    return post('booker/reassign', {
      json: params,
    }).json<BookerRescheduleReassign>()
  },
  findReassignData(reportId: string) {
    return get(`reassign/find-reassign-data/${reportId}`).json<BookerReassign | null>()
  },
  preferences() {
    return get('booker/preferences').json<BookerPreferences>()
  },
  workspacePreferences(id: string) {
    return get(`booker/preferences/${id}`).json<GlobalSettings>()
  },
  relatedTo(accountId: string) {
    return get(`booker/related-to/${accountId}`).json<BookerRelatedTo>()
  },
  workingHoursSlots(params: WorkingHoursParam) {
    return post('booker/slots/working-hours-slots', {
      json: params,
    }).json<BookerCalendarEvent[]>()
  },

  phoneCallStatus: (routeId: string) => {
    return post('booker/phone/phonecall-status', {
      json: routeId,
    }).json<{ status?: string }>()
  },

  videoCallStatus: (routeId: string, domain: string) => {
    return get(
      `booker/video/videocall-status/${domain}?routeId=${routeId}`
    ).json<BookerVideoCallStatus | null>()
  },
  connectVideoCall: (videoCallId: string) => {
    return post('booker/video/connect-videocall', {
      json: { videoCallId },
    }).json()
  },
  parseMessage: (data: PhoneParseMessageParams) =>
    post('booker/phone/parse-string', {
      json: data,
    }).json<PhoneParseString>(),

  startVideoCall: () => {
    return get('booker/start-video-call').json<BookerInitiateCall>()
  },

  startPhoneCall: (phoneNumber: string) => {
    return get(`booker/phone/initiate-call${phoneNumber}`).json<BookerInitiateCall>()
  },

  route: {
    debug(routeId: string) {
      return get(`booker/route/debug/${routeId}`).json<RulesExecutionResponse>()
    },
    settings: (domain: string, routeId: string) =>
      get(`booker/phone/route-settings/${domain}/${routeId}`).json<BookerRouteSettings>(),
  },
}

export const inboundRouter = {
  getInboundRouters: (workspaceId: string) =>
    get(`inbound-router/list/${workspaceId}`).json<{ queues: { id: string; order: number }[] }[]>(),
  getInboundRouter: (routerId: string) =>
    get(`inbound-router/router/${routerId}`).json<InboundRouter>(),
  getInboundRouterByLink: (domain: string, routerName: string) =>
    get(`inbound-router/by-link/${domain}/${routerName}`).json<InboundRouterByLink>(),
  getInboundRouterLink: () => get(`inbound-router/link`).json<string>(),
}

export const placeholders = {
  save(body: PlaceholderDAO) {
    return post('placeholders/save', {
      json: body,
      timeout: 5 * 60 * 1000, // the calendar placeholder creation takes a lot of time to complete
    }).json<Placeholder>()
  },
  byId(id: string) {
    return get(`placeholders/${id}`).json<Placeholder>()
  },
  delete(id: string) {
    return del(`placeholders/${id}`)
  },
  toggleActiveStatus(id: string, isActive: boolean) {
    return post(`placeholders/${id}/is-active`, {
      json: {
        isActive: isActive,
      },
    })
  },
  byEvent(eventId: string) {
    return get(`events/${eventId}/placeholders`).json<Placeholder[]>()
  },
}

export const calendar = {
  bookingUsers(workspaceId: string) {
    return get(`calendar/booking-users/${workspaceId}`).json<UserSearch[]>()
  },
}

export const bookingLinks = {
  save(saveRequest: BookingLinkDAO) {
    return post('bookinglinks/save', {
      json: saveRequest,
    }).json<BookingLink>()
  },
  delete(id: string) {
    return del(`bookinglinks/${id}`)
  },
  getAllPersonal() {
    return getV2('bookinglinks/personal').json<Record<string, string>>()
  },
}

export const workspaces = {
  select(workspaceId: string) {
    return put(`workspaces/${workspaceId}/select`).json()
  },
  bookingLinks(workspaceId: string) {
    return get(`workspace/${workspaceId}/bookinglinks`).json<BookingLink[]>()
  },
  changeType(workspaceId: string, productType: MeetingsProductType) {
    return put(
      `workspaces/${workspaceId}/change-type/${productType}`
    ).json<StandardSuccessResponse>()
  },
  getAll() {
    return get(`workspaces`).json<Workspace[]>()
  },
  licenseUsage(id: string) {
    return get(`workspaces/${id}/license-usage`).json<WorkspaceLicenseUsage>()
  },
  byId(id: string) {
    return get(`workspaces/${id}`).json<Workspace>()
  },
  getInfo(id: string) {
    return get(`workspaces/${id}/info`).json<Workspace>()
  },
  counters(id: string) {
    return get(`workspaces/${id}/counters`).json<{ id: string; usersWithDefualt: number }>()
  },
  manage(opts: RequestOptions = {}) {
    return get(`workspaces/manage`, { ...opts }).json<Workspace[]>()
  },
  getWithoutDeleted() {
    return get(`workspaces-all?deleted=false`).json<Workspace[]>()
  },
  getUsers(workspaceId: string) {
    return get(`team/workspace/users/${workspaceId}`).json<WorkspaceUser[]>()
  },
  recent() {
    return get(`dashboard/recent-workspaces`).json<RecentWorkspace[]>()
  },
  organizationUsers(params?: UsersAtAllParam) {
    const defaultParam = { page: 1, query: '', size: 5 }
    return post(`team/users-at-all`, {
      json: params || defaultParam,
    }).json<UsersAtAllResponse>()
  },
  organizationUser(id: string) {
    return get(`team/users/${id}`).json<UserAccess>()
  },
  users(params: TeamUsersParam) {
    return post('team/users', { json: params }).json<{ list: UserAccess[]; total: number }>()
  },
  save({ id, ...params }: SaveWorkspaceParam) {
    return post('workspaces', {
      json: {
        ...params,
        order: params.order || 0,
        workspaceId: id,
      },
    }).json<Workspace>()
  },
  delete(id: string) {
    return del(`workspaces/${id}`).json<StandardSuccessResponse>()
  },
  toggle(id: string, status: boolean) {
    return put(`workspaces/${id}/activate/${status}`).json<StandardSuccessResponse>()
  },
  assignUser(params: { activate: boolean; userId: string; workspaceId: string }) {
    return post('workspaces/assign', {
      json: {
        ...params,
      },
    }).json<StandardSuccessResponse>()
  },
  userQueues(userId: string, workspaceId: string) {
    return get(`workspaces/user-queues/${userId}/${workspaceId}`).json<string[]>()
  },
  getWorkspaceSettings(id: string) {
    return get(`booker/preferences/${id}`).json<WorkspacePreferences>()
  },
  saveWorkspaceSettings(preferences: WorkspacePreferences) {
    return put(`booker/preferences`, {
      json: preferences,
    }).json<StandardSuccessResponse>()
  },
  getInviteLink(id: string) {
    return get(`onboarding/meetings/invite-link/${id}`).json<{ link: string }>()
  },
  userWorkspaces() {
    return get('workspaces-user').json<Workspace[]>()
  },
  userWorkspacesVisible() {
    return get('workspaces-user-ib').json<Workspace[]>()
  },
  userWorkspacesHandoff() {
    return get('workspaces-user-hh').json<Workspace[]>()
  },
  getWorkspaceUsersVisibility(id: string) {
    return get(`workspaces/${id}/visibility`).json<WorkspaceVisibility['visibility']>()
  },
  setWorkspaceUsersVisibility(workspaceId: string, payload: WorkspaceVisibility) {
    return put(`workspaces/${workspaceId}/visibility`, {
      json: payload,
    }).json<WorkspaceVisibility['visibility']>()
  },
  usersAtAll(workspaceId: string) {
    return post(`team/users-at-all`, { json: { workspaceId } }).json<UsersAtAllResponse>()
  },
}

export const participant = {
  byEmail(email: string, name?: string) {
    return get(
      `participant/by-email/${email}${name ? `?name=${name}` : ''}`
    ).json<ParticipantByEmailResponse>()
  },
  byId(id: string) {
    return get(`participant/by-id/${id}`).json<ParticipantByEmailResponse>()
  },
  findInCrm(email: string) {
    return get(`participant/find-in-crm/${email}`).json<Prospect>()
  },
}

export const dashboard = {
  products() {
    return get(`dashboard/products`).json<Product[]>()
  },
  requestAccess(product: ProductTypes) {
    return get(`dashboard/request-access/${product}`).json()
  },
  getRequestedProducts() {
    return get(`dashboard/requested-products`).json<ProductTypes[]>()
  },
  requesCRMAccess() {
    return post(`dashboard/request-crm-access`).json()
  },
}

export interface TeamCsvStatusResponse {
  endTime: number
  id: string
  progress: string
  startTime: number
  userId: string
}

export const csv = {
  team: {
    download(
      timezone: string,
      workspaceId?: string,
      filters?: {
        [key: string]: any
        search?: string
      }
    ) {
      return post('team/csv', {
        json: {
          timezone,
          workspaceId,
          filters,
        },
      }).json<{ id: string }>()
    },
    status(id: string) {
      return get(`team/csv/status/${id}`).json<TeamCsvStatusResponse>()
    },
  },
}

export const business = {
  user: {
    connections() {
      return get('business/user/connections').json<ThirdPartyConnection[]>()
    },

    removeConnection(connection: ThirdPartyConnection) {
      return del(`business/user/connection/${connection.system}`).json()
    },
  },
  domain: {
    getOrganizationName(domain: string) {
      return get(`business/domain/${domain}`).json<string>()
    },
  },
}

export const organization = {
  getDetails(domain: string) {
    return get(`organization/details?domain=${domain}`).json<
      Pick<OrganizationDetails, 'website' | 'name' | 'logo'>
    >()
  },
}

export enum SalesforceServiceWorkspaceUserErrorCodes {
  ConnectionMissing = 'workspaces.crm-user-connection-missing',
  UserNotFound = 'workspaces.crm-user-not-found-for-workspace',
}

export const salesforceService = {
  workspaceUsers(payload: UserWithSalesforceRequest) {
    return post(`team/users-with-crm`, { json: payload }).json<UserWithSalesforcePaged>()
  },
  workspaceUser(workspaceId: string) {
    return get(`workspaces/${workspaceId}/crm-user`).json<SalesforceUser>()
  },
  removeWorkspaceUser(workspaceId: string) {
    return del(`workspaces/${workspaceId}/crm-user`).json()
  },
  setWorkspaceUser(workspaceId: string, userId: string) {
    return post(`workspaces/${workspaceId}/crm-user`, { json: { userId } }).json<SalesforceUser>()
  },
  eventSettings(workspaceId: string) {
    return get(`workspaces/${workspaceId}/settings/crm-event`).json<CrmEventWorkspaceSettings>()
  },
  setEventSettings(workspaceId: string, payload: CrmEventWorkspaceSettingsPayload) {
    return post(`workspaces/${workspaceId}/settings/crm-event`, {
      json: payload,
    }).json<StandardSuccessResponse>()
  },
  setTemplateEventSettings(
    workspaceId: string,
    { id: templateId, settings }: CrmTemplateEventSettingsPayload
  ) {
    return post(`workspaces/${workspaceId}/${templateId}/settings/crm-event`, {
      json: settings,
    }).json<StandardSuccessResponse>()
  },
  contactSettings(workspaceId: string) {
    return get(`workspaces/${workspaceId}/settings/crm-contact`).json<CrmContactWorkspaceSettings>()
  },
  setContactSettings(workspaceId: string, payload: CrmContactWorkspaceSettingsPayload) {
    return post(`workspaces/${workspaceId}/settings/crm-contact`, {
      json: payload,
    }).json<StandardSuccessResponse>()
  },
  setContactTemplateSettings(
    workspaceId: string,
    { id: templateId, settings }: CrmContactTemplateSettingsPayload
  ) {
    return post(`workspaces/${workspaceId}/${templateId}/settings/crm-contact`, {
      json: settings,
    }).json<StandardSuccessResponse>()
  },
  getPersonalEventSettings() {
    return get('personal/settings/crm/event').json<PersonalCrmEventSettings>()
  },

  getSalesforceQueues() {
    return get('salesforce-queues/find-all').json<SalesforceQueuesResponse>()
  },

  setPersonalEventSettings(eventSettings: PersonalCrmEventSettingsPayload) {
    return post('personal/settings/crm/event', {
      json: eventSettings,
    }).json<StandardSuccessResponse>()
  },
  setPersonalTemplateEventSettings({
    id,
    ...eventSettings
  }: PersonalTemplateCrmEventSettingsPayload) {
    return post(`personal/settings/template/${id}/crm/event`, {
      json: eventSettings,
    }).json<StandardSuccessResponse>()
  },
}

export const twilioService = {
  status() {
    return get(`twilio?uid=${new Date().getTime()}`).json<TwilioStatusResponse>()
  },
  connect(data: TwilioConnectBody) {
    return post('twilio', { json: data }).json<void>()
  },
  verifyPhone(user: TwilioPhoneVerifyPayload) {
    return get(
      `twilio/verify/start/${user.id}/${user.phone}/${user.dialCode}`
    ).json<TwilioPhoneVerifyResponse>()
  },
  phoneStatus(id: string) {
    return get(`twilio/verify/checkVerificationStatus/${id}`).json<TwilioPhoneStatusResponse>()
  },
}

export const hubspotService = {
  admin() {
    return get('admin').json<HubspotAdminResponse>()
  },
}

export const gongService = {
  refresh(isPersonal = true) {
    const path = isPersonal ? 'gong/status/refresh-current' : 'gong/refresh-users'
    return post(path, { json: {} }).json<void>()
  },
  async status(isPersonal = true) {
    if (isPersonal) {
      const personalData = await get('business/user/connections').json<ThirdPartyConnection[]>()
      return personalData.some(connection => connection.system === 'gong' && connection.connected)
    }
    const businessData = await get('business/account/connections').json<{
      gong?: { type: 'Connected' | 'Disconnected' | 'InTrouble' }
    }>()
    return businessData.gong?.type === 'Connected'
  },
  disconnect() {
    return rootApiPost('oauth2/gong/disconnect', { json: {} }).json<void>()
  },
}

export const timezoneService = {
  userSettings(userId: string) {
    return get(`timezone/${userId}`).json<TimezoneUserSettingsResponse>()
  },
  setUserSettings(userId: string, payload: TimezoneUserSettingsPayload) {
    return post(`timezone/${userId}`, {
      json: payload,
    }).json<StandardSuccessResponse>()
  },
}

export interface IntercomBookedParams {
  conversationId: string
  endTime: string
  reportId: string
  startTime: string
  timezone: string
  workspaceId: string
}

export const intercomService = {
  status() {
    return get(`intercom`).json<IntercomStatusResponse>()
  },
  booked(payload: IntercomBookedParams) {
    return post('intercom/ib/flow/booked', {
      json: payload,
    }).json()
  },
}

export const licenses = {
  all() {
    return get('licences').json<Licenses>()
  },
  accessHistory(userId: string | undefined) {
    return get(`team/user/${userId}/access-history`).json<{
      displayName?: string
      items: LicenseAccess[]
    }>()
  },
  table(payload: LicensesTableRequest) {
    return post(`licences/table`, { json: payload }).json<LicensesTableResponse>()
  },
  updateTable(usersChanged: Partial<MeetingsActiveUser>[]) {
    return post('licences/table/update', {
      json: usersChanged,
    }).json<MeetingsActiveUser[]>()
  },
  updateTableV2(params: UpdateTableV2Payload) {
    return postV2('licences/table/update', {
      json: params.usersChanged,
      searchParams: { dry: params.dry },
    }).json<UserTableUpdateEntry[]>()
  },
  fetchAllUsersBriefInfo(params: LicensesTableRequest) {
    return post('licences/tableNarrower', {
      json: params,
    }).json<ScrollableTableResponse<MeetingsActiveUserBrief>>()
  },
}

export const trialsService = {
  active() {
    return get('trials').json<TrialRaw[]>()
  },
  isSpicyTrial() {
    return trialsService
      .active()
      .then(trials => trials.some(({ id }) => id === LicensedProduct.MildMeetings))
  },
}

export const teams = {
  updateProduct(params: { product: string; status: string; userId: string }) {
    return post('team/update-product', {
      json: {
        ...params,
      },
    }).json()
  },
  assignAdmin(params: { status: boolean; userId: string }) {
    return put('team/admin', {
      json: {
        id: params.userId,
        status: params.status,
      },
    }).json<{ id: string }>()
  },

  assignWorkspaceAdmin(params: { status: boolean; userId: string; workspaceId: string }) {
    const { userId: id, ...restParams } = params
    return put('team/workspace-admin', {
      json: { ...restParams, id },
    }).json()
  },
}

export const reportingService = {
  getReport(reportId: string, domain: string) {
    return get(`reschedule/find-report/${reportId}/${domain}`).json<Report>()
  },
  listForAssignee(payload: ScrollableTableRequestV2) {
    return reportingPost('reports/for-assignee', { json: payload }).json<
      ScrollableTableResponse<Report>
    >()
  },
  generateBookedMeetingsCSV(workspaceId: string, payload: Pick<ScrollableTableRequest, 'filters'>) {
    return reportingPost(`reports/generate-booked-meetings-csv/${workspaceId}`, {
      json: payload,
    }).json<ScrollableTableRequest>()
  },
  generatePersonalBookedMeetingsCSV(
    userId: string,
    payload: Pick<ScrollableTableRequest, 'filters'>
  ) {
    return reportingPost(`reports/generate-personal-booked-meetings-csv/${userId}`, {
      json: payload,
    }).json<ScrollableTableRequest>()
  },
  listForWorkspace(workspaceId: string) {
    return (payload: ScrollableTableRequestV2) => {
      return reportingPost(`reports/workspace/${workspaceId}`, { json: payload }).json<
        ScrollableTableResponse<Report>
      >()
    }
  },
  getHistory(reportId: string) {
    return reportingGet(`reports/assignee-history/${reportId}`).json<ReportHistoryItem[]>()
  },
  retryCrmEventCreation(reportId: string) {
    return put(`reports-dashboard/retry-crm-event-creation/${reportId}`).json()
  },
  getReminders(reportId: string) {
    return get(`reminders/list-for-report/${reportId}`).json<ReminderStatus[]>()
  },
  toggleReminderSkipStatus(reportId: string, reminderId: string) {
    return put(`reminders/change-skip-status/${reportId}/${reminderId}`).json()
  },
  cancelMeeting(reportId: string) {
    return post(`booker/cancel-booking/${reportId}`).json()
  },
  cancelMeetingV2(eventId: string) {
    return rootApiGet(`cancel/${eventId}`).json<{ success: boolean }>()
  },
}

export const getEventAttendance = (eventId: string) => {
  return get(`no-show/event/${eventId}`).json<BookerCalendarEvent | null>()
}

export const setEventAttendance = (eventId: string, attended: boolean) => {
  return get(`no-show/set/${eventId}/${attended}`).json<StandardSuccessResponse>()
}

export const templates = {
  byId(id: string) {
    return get(`templates/${id}`).json<Template>()
  },
  getOwn() {
    return get(`templates-own`).json<Template[]>()
  },
  getAll() {
    return get(`templates-all`).json<Template[]>()
  },
  getAllByUser(userId: string) {
    return get(`templates-all-by-user/${userId}/last-generated`)
      .json<{ templates: { template: Template }[] }>()
      .then(({ templates }) => templates.map<Template>(({ template }) => template))
  },
  getByUser(userId: string) {
    return get(`templates-all-by-user/${userId}`).json<Template[]>()
  },
  byWorkspace(workspaceId: string) {
    return get(`templates/list/${workspaceId}`).json<Template[]>()
  },
  save(template: Partial<Template>) {
    return post('templates/save', {
      json: template,
    }).json<Template>()
  },
  delete(id: string) {
    return del(`templates/${id}`).json()
  },
  details(userId: string) {
    return get(`templates/details/${userId}`).json<TemplateDetails>()
  },
  parse(params: ParseParam) {
    return post('template/parse', { json: params }).json<ParseResponse>()
  },
  getTemplateAssigneeName: (assigneeId: string) =>
    get(`templates/details/name/${assigneeId}`).json<TemplateAssigneeName>(),
}

export const guestFormService = {
  byWorkspace(workspaceId: string) {
    return get(`online/forms/list/${workspaceId}`).json<Form[]>()
  },
  byId(id: string) {
    return get(`online/form/${id}`).json<Form>()
  },
  listAll() {
    return get('online/forms/list-all').json<Form[]>()
  },
  save(form: Form) {
    return post('online/forms/save', {
      json: form,
    }).json<StandardSuccessResponse>()
  },
  updateProspectByRouter: (domain: string, routeId: string, params: Record<string, unknown>) =>
    post(`online/update-prospect-by-router/${domain}/${routeId}`, { json: params }).text(),
}

export const onboarding = {
  account(param: CreateOnboardingAccountPayload) {
    return post('onboarding/meetings/account', {
      json: param,
    }).json()
  },
  getAccount() {
    return get('onboarding/meetings/account').json<{ account?: CreateOnboardingAccountPayload }>()
  },
  completeAction(action: SessionActions) {
    return put(`onboarding/actions/${action}`)
  },
  stepStatus() {
    return get(`onboarding/meetings/status`).json<{ step: OnboardingSteps }>()
  },
  setStepStatus(status: OnboardingSteps) {
    return post(`onboarding/meetings/status`, {
      json: {
        step: status,
      },
    }).json()
  },
  setAccountInstallationStatus(status: AccountOnboardingSessionStep) {
    return put(`onboarding/meetings/status`, {
      json: {
        status,
      },
    }).json()
  },
  setUserOnboardingStep(step: UserOnboardingSessionStep) {
    return put(`onboarding/meetings/users/status`, {
      json: {
        status: step,
      },
    }).json()
  },
  getWorkspace() {
    return get('onboarding/meetings/workspace').json<{ workspace: Workspace }>()
  },
  createWorkspace(id: string, params: SaveWorkspaceParam) {
    return put(`onboarding/meetings/workspace/${id}`, {
      json: params,
    }).json<{ workspace: Workspace }>()
  },
  saveTemplate(template: Template) {
    return post('onboarding/meetings/templates/save', {
      json: template,
    }).json<Template>()
  },
  getCRMUsers(q: string) {
    return get('onboarding/meetings/invites/search', {
      searchParams: {
        q,
      },
    }).json<{ rows: OnboardingSearchUser[] }>()
  },
  getInvites() {
    return get('onboarding/meetings/invites').json<{ invites: InviteToMeetings[] }>()
  },
  getPendingInvites(params: ScrollableTableRequest) {
    return post(`onboarding/meetings/invites/pending`, { json: params }).json<
      ScrollableTableResponse<InviteToMeetings>
    >()
  },
  updateInvite(updates: { id: string; settings: InviteToMeetings['settings'] }[]) {
    return post('onboarding/meetings/invites/update', {
      json: {
        updates,
      },
    }).json()
  },
  sendInvites(payload: { invites: DraftInviteToMeetings[]; resend: string[] }) {
    return post('onboarding/meetings/invites', { json: payload }).json<void>()
  },
  removeInvites(payload: { invites: string[] }) {
    return post('onboarding/meetings/invites/remove', { json: payload }).json<void>()
  },
  admin() {
    return get('onboarding/meetings/users/admin').json<{
      admin: { displayName: string; email: string }
    }>()
  },
  inviteAdmin(email: string) {
    return post('onboarding/meetings/invites/invite-admin', { json: { email } }).json<void>()
  },
}

export const profiles = {
  getProfileDetails(cacheKey: string) {
    return profileGet(`profile/${cacheKey}`).json<ProfileDetailsResponse>()
  },
  updateProfileImage(userId: string, url: string | null) {
    return profilePatch(`profile/${userId}`, {
      json: { image: url },
    }).json()
  },
  uploadImage(image: File) {
    const formData = new FormData()
    formData.append('image', image)
    return profileUpload('upload/image', {
      body: formData,
    }).json<string>()
  },
}

export const personal = {
  getUserLink(id: string) {
    return get(`user-link/${id}`).json<{ link: string }>()
  },
  editBookingLink(id: string, link: string) {
    return patch(`user-link/${id}/link/${link}`).json()
  },
  getUserSettings(id: string) {
    return get(`personal/settings/${id}`).json<UserSettings>()
  },
  saveSettings(settings: UserSettings) {
    return post('personal/settings', {
      json: settings,
    }).json<StandardSuccessResponse>()
  },
  getLastGeneratedLinks(userId: string) {
    return get(`templates-all-by-user/${userId}/last-generated`).json<{
      templates: LastGeneratedTemplate[]
    }>()
  },
}

export const personalPage = {
  getOrganization(domain: string) {
    return get(`personal-page/${domain}/organisation`).json<Organization>()
  },
  getOrganizationDetails() {
    return get('personal-page/organisation/details').json<OrganizationDetails>()
  },
  saveOrganizationDetails(details: OrganizationDetails) {
    return put('personal-page/organisation/details', {
      json: details,
    }).json<StandardSuccessResponse>()
  },
  getOrganizationStyles() {
    return get('personal-page/organisation/styles').json<OrganizationStyles>()
  },
  saveOrganizationStyles(styles: OrganizationStyles) {
    return put('personal-page/organisation/styles', {
      json: styles,
    }).json<OrganizationStyles>()
  },
  getOrganizationSettings() {
    return get('personal-page/organisation/settings').json<OrganizationSettings>()
  },
  saveOrganizationSettings(settings: OrganizationSettings) {
    return put('personal-page/organisation/settings', {
      json: settings,
    }).json<OrganizationSettings>()
  },
}

export const crmEntity = {
  searchAccounts(value: string) {
    return get(`crm/account/${value}/${0}`).json<AccountSearchResponse[]>()
  },
  createAccount(name: string) {
    return post('crm/account', { json: { name } }).json<string>()
  },
  createLead(entity: CreateLeadPost) {
    return post('crm/lead', { json: entity }).text()
  },
  createContact(entity: CreateContactPost) {
    return post('crm/contact', { json: entity }).text()
  },
  getLead(id: string) {
    return get(`crm/lead/${id}`).json<Prospect | StandardErrorResponse>()
  },
  getCase(id: string) {
    return get(`crm/case/${id}`).json<Case>()
  },
  getContact(id: string) {
    return get(`crm/contact/${id}`).json<Prospect | StandardErrorResponse>()
  },
}

export const reminders = {
  byWorkspaceId: (workspaceId: string) => get(`reminders/list/${workspaceId}`).json<Reminder[]>(),
  getOwn: () => get('reminders-own').json<Reminder[]>(),
  getOwnTable: () => get('reminders-own/list-view').json<ReminderTable[]>(),
  byWorkspaceIdTable: (workspaceId: string) =>
    get(`reminders/list-view/${workspaceId}`).json<ReminderTable[]>(),
  update: (payload: Reminder) => post('reminders/save', { json: payload }),
  delete: (reminderId: string) => del(`reminders/${reminderId}`),
  get: (reminderId: string) => get(`reminders/${reminderId}`).json<Reminder>(),
}

export const workspacePreferences = {
  getCss(workspaceId: string) {
    return get(`workspace-preferences/get-css/${workspaceId}`).json<{ css?: string }>()
  },
  saveCss(workspaceId: string, css: string) {
    return post(`workspace-preferences/save-css`, {
      json: {
        id: workspaceId,
        customCss: css,
      },
    }).json<{ success: boolean }>()
  },
}

export const ruleService = {
  getCrmEntities: () => get('crm/entities').json<CrmEntity[]>(),
  getCrmCustomEntities: () => get('crm/custom-entities').json<string[]>(),
  getCrmTags: () => get('crm/tags').json<string[]>(),
  getCrmFields: () => get('crm/fields/distro').json<CrmFieldsResponse>(),
  getCrmOperators: () => get('crm/operators').json<CrmOperator[]>(),
}

export const crmRouterService = {
  getRouters: (workspaceId: string) =>
    getV2(`crm-router/workspace/${workspaceId}`).json<CrmRouter[]>(),
  getRouter: (routerId: string) => getV2(`crm-router/${routerId}`).json<CrmRouter>(),
  createRouter: (workspaceId: string, payload?: Omit<CrmRouterRequest, 'id'>) =>
    postV2(`crm-router/${workspaceId}`, {
      json: payload ?? { assignmentRulesets: [], ownership: {}, name: '' },
    }).json<DraftCrmRouter>(),
  duplicateRouter: (routerId: string, payload: Partial<CrmRouterRequest> = {}) =>
    postV2(`crm-router/${routerId}/duplication`, { json: payload }).json<DraftCrmRouter>(),
  updateRouter: async (payload: CrmRouterRequest) => {
    const response = await putV2(`crm-router/${payload.id}/edit`, {
      json: payload,
    }).json<CrmRouterData>()
    return {
      ...response,
      active: response.active ?? false,
    }
  },
  publishRouter: (payload: CrmRouterRequest) =>
    putV2(`crm-router/${payload.id}/publish`, { json: payload }).json<CrmRouterData>(),
  activateRouter: (routerId: string) => putV2(`crm-router/${routerId}/activation`).json(),
  deactivateRoute: (routerId: string) => putV2(`crm-router/${routerId}/deactivation`).json(),
  deleteDraft: (routerId: string) => delV2(`crm-router/${routerId}/discard`).json<DraftCrmRouter>(),
  deleteRouter: (routerId: string) => delV2(`crm-router/${routerId}/delete`).json<CrmRouter>(),
}

export const crmStreamingService = {
  getStatus: () => get('crm-streaming/sync').json<CrmSyncStatus>(),
  sync: () => post('crm-streaming/sync').json(),
}

export const domainService = {
  getDomain: () => get('domain').json<string>(),
}
